import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';
import SEO from '../components/SEO';

import { 
  FaSearch, 
  FaFilter, 
  FaTimes, 
  FaBookOpen, 
  FaStar, 
  FaCrown, 
  FaGraduationCap,
  FaHeart,
  FaUtensils,
  FaPlane,
  FaHome,
  FaBriefcase,
  FaUsers,
  FaMusic,
  FaPalette,
  FaGamepad,
  FaLeaf,
  FaGem,
  FaLightbulb,
  FaShieldAlt,
  FaRocket,
  FaCompass,
  FaMapMarkerAlt,
  FaCalendarAlt,
  FaClock,
  FaEye,
  FaPlay,
  FaList,
  FaQuoteLeft,
  FaSeedling,
  FaTree,
  FaArrowRight,
  FaMugHot,
  FaCommentDots,
  FaUserGraduate,
  FaLanguage,
  FaPencilAlt,
  FaBrain,
  FaTrophy,
  FaRoad,
  FaFlagCheckered,
  FaCheckCircle,
  FaFacebookF,
  FaTwitter,
  FaInstagram,
  FaYoutube,
  FaEnvelope,
  FaPhoneAlt
} from 'react-icons/fa';

import './Pathways.css';
import PersianLearningPathway from '../components/PersianLearningPathway';
import '../components/PersianLearningPathway.css';

interface Pathway {
  _id: string;
  title: string;
  description: string;
  lessonCount: number;
  category?: string;
  level?: string;
  topics?: string[];
  styleTags?: string[];
}





// Enhanced categorization system for filtering
const CATEGORIES = {
  '🏠 Home & Daily Life': ['home', 'daily', 'routine', 'family', 'household'],
  '💼 Business & Work': ['business', 'work', 'office', 'professional', 'workplace'],
  '💻 Business & Digital Life': ['business', 'digital', 'technology', 'online'],
  '🍽️ Food & Dining': ['food', 'dining', 'restaurant', 'cooking', 'kitchen', 'bazaar'],
  '✈️ Travel & Tourism': ['travel', 'tourism', 'flight', 'vacation', 'explore'],
  '🎭 Culture & Arts': ['culture', 'arts', 'music', 'poetry', 'literature', 'shahnameh'],
  '💬 Conversation & Social': ['conversation', 'social', 'greeting', 'communication'],
  '💬 Conversation & Expressions': ['conversation', 'expressions', 'speaking'],
  '📚 Education & Learning': ['education', 'learning', 'grammar', 'pronunciation'],
  '🎮 Entertainment & Media': ['entertainment', 'media', 'tv', 'movies', 'games'],
  '🌿 Nature & Environment': ['nature', 'environment', 'outdoor', 'garden'],
  '💎 Special Occasions': ['holiday', 'celebration', 'festival', 'ceremony'],
  '💡 Skills & Hobbies': ['hobby', 'skill', 'activity', 'interest'],
  '🛡️ Survival & Emergency': ['survival', 'emergency', 'help', 'assistance']
};

// Mapping from frontend category names to database topic names
const CATEGORY_TO_TOPIC_MAP = {
  '🏠 Home & Daily Life': 'Home & Daily Life',
  '💼 Business & Work': 'Business & Work',
  '💻 Business & Digital Life': 'Business & Digital Life',
  '🍽️ Food & Dining': 'Food & Dining',
  '✈️ Travel & Tourism': 'Travel & Tourism',
  '🎭 Culture & Arts': 'Culture & Arts',
  '💬 Conversation & Social': 'Conversation & Social',
  '💬 Conversation & Expressions': 'Conversation & Expressions',
  '📚 Education & Learning': 'Education & Learning',
  '🎮 Entertainment & Media': 'Entertainment & Media',
  '🌿 Nature & Environment': 'Nature & Environment',
  '💎 Special Occasions': 'Cultural Celebrations',
  '💡 Skills & Hobbies': 'Skills & Hobbies',
  '🛡️ Survival & Emergency': 'Survival & Emergency'
};

const LEVELS = {
  'Beginner': { color: 'emerald', icon: '🌱', description: 'Perfect for new learners' },
  'Intermediate': { color: 'blue', icon: '🌿', description: 'For those with basic knowledge' },
  'Advanced': { color: 'purple', icon: '🌳', description: 'For experienced learners' }
};

// Featured lessons data - now using pathway IDs to fetch dynamic data
const FEATURED_LESSON_IDS = [
  {
    id: 'easy-persian-alphabet',
    pathwayId: 'easy-persian-alphabet',
    subtitle: "Easy Persian Alphabet",
    description: "Learn Persian from the very beginning! Master the Persian alphabet, vowels, and pronunciation step by step. Start with the basics and build your way to reading and writing Persian.",
    level: "Beginner",
    icon: FaGraduationCap,
    bgColor: 'alphabet-blue',
    iconColor: 'blue-600',
    stage: '🔤 Alphabet & Basics'
  },
  {
    id: 'grammar-fundamentals',
    pathwayId: 'grammar-fundamentals-pathway',
    subtitle: "Grammar Fundamentals",
    description: "Master essential Persian grammar rules and sentence structure. Learn verb conjugations, noun cases, and build a solid foundation for advanced learning.",
    level: "Beginner",
    icon: FaBookOpen,
    bgColor: 'grammar-green',
    iconColor: 'green-600',
    stage: '📝 Grammar & Structure'
  },
  {
    id: 'conversation-basics',
    pathwayId: '6875e2c70e9af9bca4a60a45',
    subtitle: "Persian Conversations with Animation",
    description: "Practice everyday conversations and learn cultural context through animated dialogues. Perfect for building confidence in speaking Persian.",
    level: "Intermediate",
    icon: FaCommentDots,
    bgColor: 'conversation-yellow',
    iconColor: 'yellow-600',
    stage: '💬 Conversation & Culture'
  },
  {
    id: 'family-conversations',
    pathwayId: 'talking-about-your-family',
    subtitle: "Talking About Your Family",
    description: "Learn how to talk about your family members, ask about others' families, and use family-related vocabulary in Farsi. Essential for building relationships.",
    level: "Beginner",
    icon: FaUsers,
    bgColor: 'family-purple',
    iconColor: 'purple-600',
    stage: '👨‍👩‍👧‍👦 Family & Relationships'
  },
  {
    id: 'kitchen-farsi',
    pathwayId: 'kitchen-farsi',
    subtitle: "🍳 Farsi in the Frying Pan",
    description: "Cook your way to Persian fluency! Learn food vocabulary, cooking instructions, and table manners while making delicious Persian dishes.",
    level: "Intermediate",
    icon: FaUtensils,
    bgColor: 'kitchen-orange',
    iconColor: 'orange-600',
    stage: '🍽️ Food & Culture'
  }
];

const Pathways: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedLevel, setSelectedLevel] = useState('');
  const [selectedTopic, setSelectedTopic] = useState('');
  const [selectedStage, setSelectedStage] = useState('');

  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [sortBy, setSortBy] = useState<'title' | 'lessons' | 'level'>('title');
  const [currentPage, setCurrentPage] = useState(1);
  const [pathwaysPerPage] = useState(12);

  // Debounce search term to prevent excessive API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm, selectedCategory, selectedLevel, selectedTopic, selectedStage, sortBy]);

  const { data: pathwaysData, isLoading, error, refetch } = useQuery({
          queryKey: ['pathways', currentPage, pathwaysPerPage, debouncedSearchTerm, selectedCategory, selectedLevel, selectedTopic, sortBy],
    queryFn: async () => {
      try {
        const params = new URLSearchParams({
          page: currentPage.toString(),
          limit: pathwaysPerPage.toString(),
          sortBy: sortBy
        });

        // Add filter parameters if they exist
        if (debouncedSearchTerm) params.append('search', debouncedSearchTerm);
        if (selectedCategory) {
          const topicName = CATEGORY_TO_TOPIC_MAP[selectedCategory as keyof typeof CATEGORY_TO_TOPIC_MAP];
          if (topicName) {
            params.append('category', topicName);
          }
        }
        if (selectedLevel) params.append('level', selectedLevel);
        if (selectedTopic) params.append('topic', selectedTopic);

        const res = await axios.get(`${API_BASE_URL}/api/pathways?${params.toString()}`);
        return res.data;
      } catch (error) {
        console.error('Failed to fetch pathways:', error);
        throw error;
      }
    },
    retry: 2,
    retryDelay: 1000,
    staleTime: 5 * 60 * 1000,
  });

  const pathways = pathwaysData?.data || [];
  const pagination = pathwaysData?.pagination;

  // Fetch featured pathway data
  const { data: featuredPathwaysData } = useQuery({
    queryKey: ['featured-pathways'],
    queryFn: async () => {
      try {
        const pathwayIds = FEATURED_LESSON_IDS.map(lesson => lesson.pathwayId);
        const promises = pathwayIds.map(async (pathwayId) => {
          try {
            if (pathwayId === 'easy-persian-alphabet') {
              const res = await axios.get(`${API_BASE_URL}/api/pathways/easy-persian-alphabet`);
              return res.data.data;
            } else {
              const res = await axios.get(`${API_BASE_URL}/api/pathways/${pathwayId}`);
              return res.data.data;
            }
          } catch (error) {
            console.error(`Failed to fetch pathway ${pathwayId}:`, error);
            return null;
          }
        });
        
        const results = await Promise.all(promises);
        return results.filter(Boolean);
      } catch (error) {
        console.error('Failed to fetch featured pathways:', error);
        return [];
      }
    },
    staleTime: 10 * 60 * 1000,
  });

  const featuredPathways = featuredPathwaysData || [];

  // Organize pathways by learning stages


  const allTopics = useMemo(() => {
    if (!pathways) return [];
    const topics = new Set<string>();
    pathways.forEach((pathway: Pathway) => {
      pathway.topics?.forEach(topic => topics.add(topic));
    });
    return Array.from(topics).sort();
  }, [pathways]);

  // Filter pathways based on selected stage
  const filteredPathways = useMemo(() => {
    return pathways;
  }, [pathways]);

  const clearFilters = useCallback(() => {
    setSearchTerm('');
    setSelectedCategory('');
    setSelectedLevel('');
    setSelectedTopic('');

    setCurrentPage(1);
  }, []);

  const getPathwayIcon = (pathway: Pathway) => {
    const title = pathway.title.toLowerCase();
    const description = pathway.description.toLowerCase();
    
    if (title.includes('home') || description.includes('home')) return <FaHome />;
    if (title.includes('food') || title.includes('kitchen') || title.includes('cooking')) return <FaUtensils />;
    if (title.includes('travel') || title.includes('flight')) return <FaPlane />;
    if (title.includes('business') || title.includes('work')) return <FaBriefcase />;
    if (title.includes('culture') || title.includes('arts')) return <FaPalette />;
    if (title.includes('music')) return <FaMusic />;
    if (title.includes('game') || title.includes('play')) return <FaGamepad />;
    if (title.includes('nature') || title.includes('garden')) return <FaLeaf />;
    if (title.includes('poetry') || title.includes('literature')) return <FaGem />;
    if (title.includes('conversation') || title.includes('social')) return <FaUsers />;
    if (title.includes('education') || title.includes('learning')) return <FaGraduationCap />;
    if (title.includes('emergency') || title.includes('help')) return <FaShieldAlt />;
    if (title.includes('holiday') || title.includes('celebration')) return <FaCalendarAlt />;
    if (title.includes('hobby') || title.includes('skill')) return <FaLightbulb />;
    if (title.includes('adventure') || title.includes('explore')) return <FaCompass />;
    if (title.includes('time') || title.includes('clock')) return <FaClock />;
    if (title.includes('location') || title.includes('place')) return <FaMapMarkerAlt />;
    if (title.includes('rocket') || title.includes('launch')) return <FaRocket />;
    if (title.includes('grammar') || title.includes('verb') || title.includes('tense')) return <FaPencilAlt />;
    if (title.includes('alphabet') || title.includes('letter')) return <FaLanguage />;
    
    return <FaBookOpen />;
  };

  const getPathwayColor = (pathway: Pathway) => {
    if (pathway.level === 'Beginner') {
      return 'from-green-500 to-green-600';
    } else if (pathway.level === 'Intermediate') {
      return 'from-yellow-500 to-yellow-600';
    } else if (pathway.level === 'Advanced') {
      return 'from-red-500 to-red-600';
    }
    
    return 'from-gray-500 to-gray-600';
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="loading-spinner w-12 h-12 border-4 border-persian-blue border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-600">Loading pathways...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Pathways Temporarily Unavailable</h2>
          <p className="text-gray-600 mb-6">
            We're experiencing technical difficulties with our pathways service. Our team is working to fix this issue.
          </p>
          <div className="space-y-4">
            <button 
              onClick={() => window.location.reload()} 
              className="w-full px-6 py-3 bg-persian-blue text-white rounded-full hover:bg-blue-700 transition font-medium"
            >
              Try Again
            </button>
            <div className="text-sm text-gray-500">
              <p>In the meantime, you can:</p>
              <ul className="mt-2 space-y-1">
                <li>• Explore our <a href="/flashcards" className="text-persian-blue hover:underline">Flashcards</a></li>
                <li>• Check out our <a href="/word-of-the-day" className="text-persian-blue hover:underline">Word of the Day</a></li>
                <li>• Visit our <a href="/blog" className="text-persian-blue hover:underline">Blog</a> for learning tips</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEO 
        title="Farsi Learning Pathways | Persian Language Courses | پیروز Pirooz"
        description="Explore comprehensive Farsi learning pathways and Persian language courses at پیروز Pirooz. From beginner to advanced levels - alphabet learning, vocabulary building, grammar mastery, and cultural immersion. Start your Persian journey today!"
        keywords={[
          // Core Keywords (High-Intent Learners) - Primary Variations
          'farsi learning paths', 'persian course structure', 'farsi curriculum', 'persian lessons',
          'farsi beginner course', 'persian alphabet course', 'farsi vocabulary path', 'persian grammar',
          'structured farsi learning', 'persian language progression', 'farsi course catalog',
          'farsi learning program', 'persian learning program', 'farsi course system',
          
          // Core Keywords - Secondary Variations
          'farsi learning method', 'persian learning method', 'farsi language system',
          'persian language system', 'farsi learning approach', 'persian learning approach',
          'farsi course curriculum', 'persian course curriculum', 'farsi learning pathway',
          
          // Long-Tail Keywords (Specific Needs) - Primary Variations
          'how to learn farsi fast', 'easiest way to learn persian', 'learn farsi in 30 days',
          'learn persian for travel', 'conversational farsi phrases', 'farsi grammar explained',
          'persian numbers for beginners', 'farsi flashcards online', 'learn to read and write persian',
          'farsi pronunciation guide', 'persian alphabet pronunciation', 'farsi vocabulary builder',
          
          // Long-Tail Keywords - Secondary Variations
          'fastest way to learn farsi', 'quick farsi learning', 'accelerated persian course',
          'persian for travelers', 'farsi travel phrases', 'iran travel language',
          'farsi grammar rules', 'persian grammar tutorial', 'farsi grammar lessons',
          'persian numbers 1-100', 'farsi counting', 'persian number system',
          'farsi flashcard app', 'persian flashcard deck', 'farsi vocabulary cards',
          
          // Cultural & Contextual Keywords - Primary Variations
          'learn farsi for business', 'persian poetry and language', 'farsi words in daily life',
          'learn persian through music', 'persian culture and language learning',
          'farsi for heritage learners', 'iranian language learning resources',
          'persian culture immersion', 'farsi cultural context', 'persian traditions and language',
          
          // Cultural & Contextual Keywords - Secondary Variations
          'business farsi', 'persian business language', 'farsi for professionals',
          'persian poetry lessons', 'farsi literature', 'persian poetry analysis',
          'daily farsi phrases', 'persian everyday words', 'farsi common expressions',
          'persian music lessons', 'farsi through songs', 'persian cultural music',
          
          // Skill Level Keywords
          'farsi beginner lessons', 'persian beginner lessons', 'farsi intermediate lessons',
          'persian intermediate lessons', 'farsi advanced lessons', 'persian advanced lessons',
          'farsi beginner course', 'persian beginner course', 'farsi intermediate course',
          'persian intermediate course', 'farsi advanced course', 'persian advanced course',
          'farsi for absolute beginners', 'persian for absolute beginners', 'farsi for kids',
          
          // Learning Method Keywords
          'farsi speaking practice', 'persian speaking practice', 'farsi listening practice',
          'persian listening practice', 'farsi reading practice', 'persian reading practice',
          'farsi writing practice', 'persian writing practice', 'farsi comprehension',
          'persian comprehension', 'farsi fluency', 'persian fluency', 'farsi proficiency',
          
          // Technology Keywords
          'farsi online learning', 'persian online learning', 'farsi digital learning',
          'persian digital learning', 'farsi virtual lessons', 'persian virtual lessons',
          'farsi video lessons', 'persian video lessons', 'farsi interactive lessons',
          'persian interactive lessons', 'farsi gamified learning', 'persian gamified learning',
          
          // Community & Social Keywords
          'farsi learning community', 'persian learning community', 'farsi language exchange',
          'persian language exchange', 'farsi study group', 'persian study group',
          'farsi language partner', 'persian language partner', 'farsi conversation partner',
          'persian conversation partner', 'farsi learning forum', 'persian learning forum'
        ]}
        canonicalUrl="/pathways"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "ItemList",
          "name": "Farsi Learning Pathways",
          "description": "Comprehensive structured courses for learning Farsi (Persian) language",
          "url": "https://vajak.com/pathways",
          "numberOfItems": filteredPathways.length,
          "itemListElement": filteredPathways.slice(0, 10).map((pathway, index) => ({
            "@type": "Course",
            "position": index + 1,
            "name": pathway.title,
            "description": pathway.description,
            "provider": {
              "@type": "EducationalOrganization",
              "name": "پیروز Pirooz"
            },
            "courseCode": pathway.id,
            "educationalLevel": pathway.difficulty || "Beginner",
            "inLanguage": ["en", "fa"],
            "url": `https://vajak.com/pathways/${pathway.id}`
          }))
        }}
      />
      <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="hero-gradient py-6 md:py-12">
        <div className="container mx-auto">
          <div className="text-center mb-8 md:mb-12">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-gray-800 mb-4 md:mb-6 leading-tight">
              Your <span className="text-persian-blue">Persian Learning Journey</span>
            </h1>
            <p className="text-lg sm:text-xl md:text-2xl text-gray-600 max-w-3xl mx-auto mb-6 md:mb-8 px-4">
              Follow our structured learning progression from alphabet mastery to advanced cultural fluency.
            </p>
            <p className="text-base md:text-lg text-gray-500 max-w-2xl mx-auto px-4">
              Each stage builds upon the previous one, ensuring you develop a solid foundation in Persian language and culture.
            </p>
          </div>
        </div>
      </section>

      {/* Learning Progression Overview - New Persian Learning Pathway */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <PersianLearningPathway />
        </div>
      </section>

      {/* Featured Lessons Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-800 mb-4">Start Your Journey</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">Begin with these essential pathways to build your Persian foundation.</p>
          </div>
          
          <div className="featured-lessons-grid grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 lg:gap-8">
            {FEATURED_LESSON_IDS.map((lessonConfig, index) => {
              const pathway = featuredPathways.find(p => p._id === lessonConfig.pathwayId);
              if (!pathway) return null;
              
              return (
                <div key={lessonConfig.id} className="lesson-card group bg-white rounded-2xl shadow-lg hover:shadow-2xl overflow-hidden transition-all duration-500 ease-in-out transform hover:-translate-y-2 border border-gray-100 hover:border-gray-200">
                  <div className={`h-40 sm:h-44 md:h-48 overflow-hidden ${lessonConfig.bgColor} flex items-center justify-center relative`}>
                    <div className="absolute inset-0 bg-gradient-to-br from-black/10 to-transparent"></div>
                    <div className="text-center p-3 md:p-4 relative z-10">
                      <lessonConfig.icon className={`lesson-icon text-4xl sm:text-5xl md:text-6xl ${lessonConfig.iconColor} mb-2 md:mb-4 transform group-hover:scale-110 transition-transform duration-300`} />
                      <h3 className="text-lg sm:text-xl font-bold text-black drop-shadow-sm leading-tight">{lessonConfig.subtitle}</h3>
                      <p className="text-xs sm:text-sm text-gray-700 mt-1 md:mt-2">{lessonConfig.stage}</p>
                    </div>
                  </div>
                  <div className="p-4 md:p-6">
                    <div className="flex justify-between items-start mb-3 md:mb-4">
                      <span className={`px-2 md:px-4 py-1 md:py-1.5 bg-${pathway.level === 'Beginner' ? 'green' : 'yellow'}-100 text-${pathway.level === 'Beginner' ? 'green' : 'yellow'}-800 text-xs font-bold rounded-full border border-${pathway.level === 'Beginner' ? 'green' : 'yellow'}-200`}>
                        {pathway.level}
                      </span>
                      <span className="text-xs md:text-sm text-gray-500 font-medium flex items-center">
                        <FaBookOpen className="mr-1 text-xs" />
                        {pathway.lessonCount} lesson{pathway.lessonCount !== 1 ? 's' : ''}
                      </span>
                    </div>
                    <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2 md:mb-3 leading-tight">{pathway.title}</h3>
                    <p className="text-sm md:text-base text-gray-600 mb-4 md:mb-6 leading-relaxed">{lessonConfig.description}</p>
                    <Link
                      to={lessonConfig.pathwayId === 'easy-persian-alphabet' ? '/easy-persian-alphabet' : `/pathways/${lessonConfig.pathwayId}`}
                      className="featured-lesson-btn touch-target w-full inline-flex items-center justify-center px-4 md:px-6 py-3 text-sm font-bold text-white rounded-xl"
                    >
                      <div className="btn-content">
                        <span>Start Learning</span>
                        <FaArrowRight className="btn-icon text-xs ml-2" />
                      </div>
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Levels Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-800 mb-4">Find Your Level</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">We have content tailored for every stage of your Farsi learning journey.</p>
          </div>
          
          <div className="levels-grid grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {Object.entries(LEVELS).map(([level, config]) => (
              <div key={level} className={`bg-${config.color}-50 rounded-xl p-6 md:p-8 text-center border-b-4 border-${config.color}-400`}>
                <div className={`w-16 h-16 md:w-20 md:h-20 rounded-full bg-${config.color}-100 flex items-center justify-center mx-auto mb-4 md:mb-6`}>
                  {level === 'Beginner' ? <FaSeedling className="text-2xl md:text-3xl text-green-600" /> :
                   level === 'Intermediate' ? <FaLeaf className="text-2xl md:text-3xl text-yellow-600" /> :
                   <FaTree className="text-2xl md:text-3xl text-red-600" />}
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-black mb-3 md:mb-4">{level}</h3>
                <p className="text-sm md:text-base text-gray-600 mb-4 md:mb-6">{config.description}</p>
                <button 
                  onClick={() => setSelectedLevel(level)}
                  className={`touch-target px-4 md:px-6 py-2 md:py-3 rounded-full transition font-medium text-sm md:text-base ${
                    level === 'Beginner' 
                      ? 'bg-emerald-800 text-white hover:bg-emerald-900' 
                      : `bg-${config.color}-600 text-white hover:bg-${config.color}-700`
                  }`}
                >
                  Explore {level} Content
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonial Section */}
      <section className="py-16 bg-persian-blue text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <FaQuoteLeft className="text-3xl text-blue-200 mb-6 mx-auto" />
            <p className="text-xl md:text-2xl mb-8">
              "The structured progression from alphabet to grammar to conversation made learning Persian feel natural and achievable. I love how each stage builds on the previous one!"
            </p>
            <div className="flex items-center justify-center space-x-4">
              <div className="w-12 h-12 rounded-full bg-blue-200 flex items-center justify-center">
                <FaUserGraduate className="text-persian-blue text-xl" />
              </div>
              <div className="text-left">
                <h4 className="font-semibold">Sarah Johnson</h4>
                <p className="text-blue-200 text-sm">Student from Canada</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Filter Section */}
      <section className="py-8 bg-gray-100">
        <div className="container mx-auto px-4">
          {/* Loading State */}
          {isLoading && (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-persian-blue"></div>
              <p className="mt-4 text-gray-600">Loading pathways...</p>
            </div>
          )}

          {/* Search Processing State */}
          {searchTerm && searchTerm !== debouncedSearchTerm && (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-persian-blue"></div>
              <p className="mt-2 text-gray-600 text-sm">Searching pathways...</p>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="text-center py-12">
              <div className="text-red-500 text-xl mb-4">⚠️ Failed to load pathways</div>
              <p className="text-gray-600 mb-4">Please try again later.</p>
              <button
                onClick={() => refetch()}
                className="px-6 py-3 bg-persian-blue text-white rounded-lg hover:bg-blue-700 transition"
              >
                Retry
              </button>
            </div>
          )}

          {/* Search and Sort Bar */}
          <div className="search-sort-bar flex flex-col md:flex-row gap-3 md:gap-4 mb-6">
            <div className="relative flex-1">
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search pathways..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input w-full pl-10 pr-12 py-3 md:py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-persian-blue focus:border-transparent transition-all duration-200 hover:border-gray-400"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="touch-target absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors p-1"
                >
                  <FaTimes />
                </button>
              )}
              {searchTerm && searchTerm !== debouncedSearchTerm && (
                <div className="absolute right-12 top-1/2 transform -translate-y-1/2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-persian-blue"></div>
                </div>
              )}
            </div>
            
            <div className="flex gap-2 justify-between md:justify-start">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as 'title' | 'lessons' | 'level')}
                className="flex-1 md:flex-none px-3 md:px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-persian-blue focus:border-transparent text-sm md:text-base"
              >
                <option value="title">Sort by Title</option>
                <option value="lessons">Sort by Lessons</option>
                <option value="level">Sort by Level</option>
              </select>
              
              <div className="flex gap-1 md:gap-2">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`touch-target px-3 md:px-4 py-3 rounded-lg border transition ${viewMode === 'grid' ? 'bg-persian-blue text-white border-persian-blue' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
                >
                  <FaEye className="text-sm" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`touch-target px-3 md:px-4 py-3 rounded-lg border transition ${viewMode === 'list' ? 'bg-persian-blue text-white border-persian-blue' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
                >
                  <FaList className="text-sm" />
                </button>
              </div>
            </div>
          </div>

          {/* Active Filters */}
          {(searchTerm || selectedCategory || selectedLevel || selectedTopic) && (
            <div className="flex flex-wrap gap-2 mb-6">
              {searchTerm && (
                <span className="tag bg-blue-100 text-blue-900 px-3 py-1 rounded-full text-sm flex items-center gap-2">
                  Search: "{searchTerm}"
                  <button onClick={() => setSearchTerm('')} className="hover:text-blue-700">
                    <FaTimes />
                  </button>
                </span>
              )}

              {selectedCategory && (
                <span className="tag bg-green-100 text-green-900 px-3 py-1 rounded-full text-sm flex items-center gap-2">
                  Category: {CATEGORY_TO_TOPIC_MAP[selectedCategory as keyof typeof CATEGORY_TO_TOPIC_MAP] || selectedCategory}
                  <button onClick={() => setSelectedCategory('')} className="hover:text-green-700">
                    <FaTimes />
                  </button>
                </span>
              )}
              {selectedLevel && (
                <span className="tag bg-purple-100 text-purple-900 px-3 py-1 rounded-full text-sm flex items-center gap-2">
                  Level: {selectedLevel}
                  <button onClick={() => setSelectedLevel('')} className="hover:text-purple-700">
                    <FaTimes />
                  </button>
                </span>
              )}
              {selectedTopic && (
                <span className="tag bg-orange-100 text-orange-900 px-3 py-1 rounded-full text-sm flex items-center gap-2">
                  Topic: {selectedTopic}
                  <button onClick={() => setSelectedTopic('')} className="hover:text-orange-700">
                    <FaTimes />
                  </button>
                </span>
              )}
              <button
                onClick={clearFilters}
                className="tag bg-gray-100 text-gray-900 px-3 py-1 rounded-full text-sm hover:bg-gray-200 transition"
              >
                Clear All
              </button>
            </div>
          )}

          {/* Level Filters */}
          <div className="level-filters flex flex-col md:flex-row flex-wrap gap-3 md:gap-4 mb-6 md:mb-8">
            {Object.entries(LEVELS).map(([level, config]) => (
              <button
                key={level}
                onClick={() => setSelectedLevel(selectedLevel === level ? '' : level)}
                className={`level-button touch-target px-4 md:px-6 py-3 md:py-3 rounded-lg font-medium transition text-left md:text-center ${
                  selectedLevel === level 
                    ? `text-white shadow-lg ${
                        level === 'Beginner' ? 'bg-green-700' :
                        level === 'Intermediate' ? 'bg-blue-600' :
                        'bg-purple-600'
                      }`
                    : 'bg-white hover:bg-gray-50 border border-gray-200 text-gray-900'
                }`}
              >
                <div className="flex items-center md:flex-col md:items-center">
                  <span className="mr-2 md:mr-0 md:mb-1 text-lg">{config.icon}</span>
                  <div>
                    <div className="font-semibold">{level}</div>
                    <div className="text-xs opacity-75 mt-0 md:mt-1">{config.description}</div>
                  </div>
                </div>
              </button>
            ))}
          </div>

          {/* Topic Filters */}
          {allTopics.length > 0 && (
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Filter by Topic</h3>
              <div className="flex flex-wrap gap-2">
                {allTopics.map((topic) => (
                  <button
                    key={topic}
                    onClick={() => setSelectedTopic(selectedTopic === topic ? '' : topic)}
                    className={`topic-button px-4 py-2 rounded-full text-sm font-medium transition ${selectedTopic === topic ? 'active bg-persian-blue text-white' : 'bg-white hover:bg-gray-50 border border-gray-200 text-gray-900'}`}
                  >
                    {topic}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Pathways Display */}
      <section className="py-8">
        <div className="container mx-auto px-4">
          {filteredPathways.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">🔍</div>
              <h3 className="text-2xl font-bold text-gray-800 mb-2">No pathways found</h3>
              <p className="text-gray-600 mb-6">Try adjusting your filters or search terms.</p>
              <button
                onClick={clearFilters}
                className="px-6 py-3 bg-persian-blue text-white rounded-full hover:bg-blue-700 transition"
              >
                Clear All Filters
              </button>
            </div>
          ) : (
            <>
              <div className="flex justify-start items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-800 text-left">
                  {filteredPathways.length} Pathway{filteredPathways.length !== 1 ? 's' : ''} Found
                </h2>
              </div>
              
              <div className={`pathway-grid ${viewMode === 'grid' ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6' : 'space-y-4'} ${isLoading ? 'loading' : ''}`}>
                {filteredPathways.map((pathway, index) => (
                  <article
                    key={pathway._id}
                    className={`pathway-card bg-white rounded-xl shadow-md overflow-hidden transition duration-300 grid-item`}
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    <div className={`h-2 bg-gradient-to-r ${getPathwayColor(pathway)}`}></div>
                    <div className="p-4 md:p-6">
                      <div className="flex items-center mb-4">
                        <div className={`w-12 h-12 rounded-full bg-gradient-to-r ${getPathwayColor(pathway)} flex items-center justify-center mr-4 pathway-icon`}>
                          <span className="text-white text-lg">
                            {getPathwayIcon(pathway)}
                          </span>
                        </div>
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-black mb-1">{pathway.title}</h3>
                          <p className="text-sm text-gray-700 line-clamp-2">{pathway.description}</p>
                        </div>
                      </div>
                      
                      <div className="flex flex-wrap gap-2 mb-4">
                        {pathway.level && (
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                            pathway.level === 'Beginner' ? 'bg-green-100 text-green-900' :
                            pathway.level === 'Intermediate' ? 'bg-yellow-100 text-yellow-900' :
                            'bg-red-100 text-red-900'
                          }`}>
                            {pathway.level}
                          </span>
                        )}
                        <span className="px-2 py-1 bg-blue-100 text-blue-900 text-xs font-medium rounded-full">
                          {pathway.lessonCount} lesson{pathway.lessonCount !== 1 ? 's' : ''}
                        </span>
                        {pathway.topics?.slice(0, 2).map((topic, idx) => (
                          <span key={idx} className="px-2 py-1 bg-gray-100 text-gray-900 text-xs font-medium rounded-full">
                            {topic}
                          </span>
                        ))}
                      </div>
                      
                                              <Link
                        to={`/pathways/${pathway._id}`}
                        className="action-button touch-target w-full py-3 bg-persian-blue text-white rounded-lg hover:bg-blue-700 transition font-medium text-center block flex items-center justify-center gap-2"
                      >
                        <FaPlay className="text-sm" />
                        <span className="text-sm md:text-base">Start Learning</span>
                      </Link>
                    </div>
                  </article>
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      {/* Pagination Controls */}
      {pagination && pagination.totalPages > 1 && (
        <section className="py-8 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="flex justify-center items-center space-x-4">
              {/* Previous Button - Left Side */}
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={!pagination.hasPrev}
                className={`px-4 py-2 rounded-lg font-medium transition ${
                  pagination.hasPrev
                    ? 'bg-persian-blue text-white hover:bg-blue-700'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                Previous
              </button>
              
              {/* Page Numbers - Center */}
              <div className="flex items-center space-x-2">
                {(() => {
                  const totalPages = pagination.totalPages;
                  const current = currentPage;
                  const maxVisible = 5;
                  
                  let startPage = Math.max(1, current - Math.floor(maxVisible / 2));
                  let endPage = Math.min(totalPages, startPage + maxVisible - 1);
                  
                  // Adjust if we're near the end
                  if (endPage - startPage < maxVisible - 1) {
                    startPage = Math.max(1, endPage - maxVisible + 1);
                  }
                  
                  const pages = [];
                  for (let i = startPage; i <= endPage; i++) {
                    pages.push(i);
                  }
                  
                  return pages.map(pageNum => (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`px-3 py-2 rounded-lg font-medium transition ${
                        currentPage === pageNum
                          ? 'bg-persian-blue text-white'
                          : 'bg-white text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      {pageNum}
                    </button>
                  ));
                })()}
                
                {/* Ellipsis for more pages */}
                {pagination.totalPages > 5 && currentPage < pagination.totalPages - 2 && (
                  <span className="px-3 py-2 text-gray-500">...</span>
                )}
              </div>
              
              {/* Next Button - Right Side */}
              <button
                onClick={() => setCurrentPage(prev => Math.min(pagination.totalPages, prev + 1))}
                disabled={!pagination.hasNext}
                className={`px-4 py-2 rounded-lg font-medium transition ${
                  pagination.hasNext
                    ? 'bg-persian-blue text-white hover:bg-blue-700'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                Next
              </button>
            </div>
            
            {/* Page Info */}
            <div className="text-center mt-4 text-gray-600">
              Showing {((currentPage - 1) * pagination.limit) + 1} to {Math.min(currentPage * pagination.limit, pagination.total)} of {pagination.total} pathways
              {pagination.total > 0 && (
                <span className="block text-sm text-gray-500 mt-1">
                  {filteredPathways.length} pathways on this page
                </span>
              )}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="cta-section py-12 md:py-16 bg-gray-900 text-white">
        <div className="container mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 md:mb-6 leading-tight">Ready to Start Your Persian Journey?</h2>
          <p className="text-lg md:text-xl text-gray-300 max-w-2xl mx-auto mb-6 md:mb-8 px-4">
            Follow our structured learning progression and join thousands of learners discovering the beauty of Farsi language and culture.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4 sm:gap-4 px-4">
            <Link
              to="/register"
              className="touch-target px-6 md:px-8 py-3 md:py-4 rounded-full bg-persian-blue text-white hover:bg-blue-800 transition font-medium shadow-lg text-sm md:text-base"
            >
              Create Free Account
            </Link>
            <Link
              to="/blog"
              className="touch-target px-6 md:px-8 py-3 md:py-4 rounded-full border border-white text-white hover:bg-white hover:text-gray-900 transition font-medium text-sm md:text-base"
            >
              Take a Tour
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8 md:py-12">
        <div className="container mx-auto">
          <div className="footer-grid grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
            <div className="sm:col-span-2 md:col-span-1">
              <div className="flex items-center justify-center sm:justify-start space-x-2 mb-4">
                <FaBookOpen className="text-2xl md:text-3xl gradient-text" />
                <h3 className="text-xl md:text-2xl font-bold">Vajak</h3>
              </div>
              <p className="text-gray-400 text-sm md:text-base leading-relaxed text-center sm:text-left">Your gateway to Persian language and culture. Learn Farsi while discovering Iran's rich heritage with Pirooz.</p>
            </div>
            <div>
              <h4 className="text-base md:text-lg font-semibold mb-3 md:mb-4 text-center sm:text-left">Quick Links</h4>
              <ul className="space-y-2 text-center sm:text-left">
                <li><Link to="/" className="text-gray-400 hover:text-white transition text-sm md:text-base touch-target inline-block py-1">Home</Link></li>
                <li><Link to="/blog" className="text-gray-400 hover:text-white transition text-sm md:text-base touch-target inline-block py-1">Blog</Link></li>
                <li><Link to="/pathways" className="text-gray-400 hover:text-white transition text-sm md:text-base touch-target inline-block py-1">Courses</Link></li>
                <li><Link to="/about" className="text-gray-400 hover:text-white transition text-sm md:text-base touch-target inline-block py-1">About Us</Link></li>
                <li><Link to="/contact" className="text-gray-400 hover:text-amber-400 text-sm md:text-base touch-target inline-block py-1">Contact</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-base md:text-lg font-semibold mb-3 md:mb-4 text-center sm:text-left">Categories</h4>
              <ul className="space-y-2 text-center sm:text-left">
                <li><Link to="/blog" className="text-gray-400 hover:text-white transition text-sm md:text-base touch-target inline-block py-1">Language Learning</Link></li>
                <li><Link to="/blog" className="text-gray-400 hover:text-white transition text-sm md:text-base touch-target inline-block py-1">Persian History</Link></li>
                <li><Link to="/blog" className="text-gray-400 hover:text-white transition text-sm md:text-base touch-target inline-block py-1">Iranian Culture</Link></li>
                <li><Link to="/blog" className="text-gray-400 hover:text-white transition text-sm md:text-base touch-target inline-block py-1">Persian Literature</Link></li>
                <li><Link to="/blog" className="text-gray-400 hover:text-white transition text-sm md:text-base touch-target inline-block py-1">Travel Guides</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-base md:text-lg font-semibold mb-3 md:mb-4 text-center sm:text-left">Contact Us</h4>
              <ul className="space-y-2 mb-4 md:mb-6">
                <li className="flex items-center justify-center sm:justify-start space-x-2">
                  <FaEnvelope className="text-purple-400 text-sm flex-shrink-0" />
                  <a href="mailto:support@pingquack.com" className="text-gray-400 hover:text-white transition text-sm md:text-base break-all">support@pingquack.com</a>
                </li>
                <li className="flex items-center justify-center sm:justify-start space-x-2">
                  <FaPhoneAlt className="text-purple-400 text-sm flex-shrink-0" />
                  <a href="tel:+14245378243" className="text-gray-400 hover:text-white transition text-sm md:text-base">+1 (424) 537-8243</a>
                </li>
                <li className="flex items-center justify-center sm:justify-start space-x-2">
                  <FaMapMarkerAlt className="text-purple-400 text-sm flex-shrink-0" />
                  <span className="text-gray-400 text-sm md:text-base">Laguna Hills, CA</span>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-6 md:mt-8 pt-6 md:pt-8 text-center text-gray-400">
            <p className="text-sm md:text-base">&copy; 2024 پیروز Pirooz. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* Gradient text style */}
      <style>{`
        .gradient-text {
          background: linear-gradient(90deg, #8B5CF6, #EC4899);
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
        }
      `}</style>
    </div>
    </>
  );
};

export default Pathways;
